import React, { ReactNode, useMemo, useState } from 'react';
import { Popover, Space, Typography } from 'antd';
import type { PopoverProps } from 'antd';
import Button from 'components/button';
import {
  FlexColumn,
  OptionDescription,
  OptionLabel,
  OptionMeta,
  StyledRadioWrapper,
} from './RadioPopoverDropdown.styles';

const { Text } = Typography;

export type RadioPopoverOption = {
  value: string;
  label: ReactNode;
  description?: ReactNode;
  meta?: ReactNode;
};

type RadioPopoverActions = {
  confirmLabel?: string;
  cancelLabel?: string;
  size?: 'small';
  showCancel?: boolean;
  fullWidth?: boolean;
  disableWhenUnchanged?: boolean;
};

type RadioPopoverDropdownProps = {
  value: string;
  options: RadioPopoverOption[];
  onChange: (value: string) => void;
  title?: ReactNode;
  footer?: ReactNode | ((selectedValue: string) => ReactNode);
  disabled?: boolean;
  variant?: 'simple' | 'card';
  actions?: RadioPopoverActions;
  popoverProps?: Omit<
    PopoverProps,
    'content' | 'children' | 'onVisibleChange' | 'visible' | 'trigger'
  >;
  renderTrigger: (args: {
    open: boolean;
    onOpen: () => void;
    onClose: () => void;
    disabled?: boolean;
  }) => ReactNode;
};

const RadioPopoverDropdown = ({
  value,
  options,
  onChange,
  title,
  footer,
  disabled,
  variant = 'simple',
  actions,
  popoverProps,
  renderTrigger,
}: RadioPopoverDropdownProps): JSX.Element => {
  const [selectedValue, setSelectedValue] = useState(value);
  const [open, setOpen] = useState(false);

  const handleCancel = () => {
    setSelectedValue(value);
    setOpen(false);
  };

  const handleUpdate = () => {
    if (selectedValue !== value) {
      onChange(selectedValue);
    }
    setOpen(false);
  };

  const onVisibleChange = (visible: boolean) => {
    if (disabled) return;
    if (visible) {
      setSelectedValue(value);
      setOpen(true);
    } else {
      handleCancel();
    }
  };

  const titleNode = typeof title === 'string' ? <Text strong>{title}</Text> : title ?? null;
  const resolvedFooter = typeof footer === 'function' ? footer(selectedValue) : footer ?? null;

  const {
    confirmLabel = 'Update',
    cancelLabel = 'Cancel',
    size,
    showCancel = true,
    fullWidth = false,
    disableWhenUnchanged = false,
  } = actions ?? {};

  const confirmIsDisabled = disableWhenUnchanged && selectedValue === value;

  const content = useMemo(
    () => (
      <FlexColumn>
        {titleNode}
        <FlexColumn
          $gap={variant === 'card' ? 12 : undefined}
          $gapSm={variant === 'card' ? 12 : undefined}
        >
          {options.map((opt) => (
            <StyledRadioWrapper
              key={opt.value}
              $variant={variant}
              $checked={selectedValue === opt.value}
            >
              <input
                type="radio"
                name="radio-popover-dropdown"
                value={opt.value}
                checked={selectedValue === opt.value}
                onChange={(e) => setSelectedValue(e.target.value)}
              />
              <div>
                {typeof opt.label === 'string' ? (
                  <OptionLabel $variant={variant}>{opt.label}</OptionLabel>
                ) : (
                  opt.label
                )}
                {opt.description &&
                  (typeof opt.description === 'string' ? (
                    <OptionDescription>{opt.description}</OptionDescription>
                  ) : (
                    opt.description
                  ))}
                {opt.meta &&
                  (typeof opt.meta === 'string' ? <OptionMeta>{opt.meta}</OptionMeta> : opt.meta)}
              </div>
            </StyledRadioWrapper>
          ))}
        </FlexColumn>
        {resolvedFooter && <div>{resolvedFooter}</div>}
        {showCancel ? (
          <Space>
            <Button $secondary size={size} onClick={handleCancel}>
              {cancelLabel}
            </Button>
            <Button $primary size={size} onClick={handleUpdate} disabled={confirmIsDisabled}>
              {confirmLabel}
            </Button>
          </Space>
        ) : (
          <Button
            $primary
            $full={fullWidth}
            size={size}
            onClick={handleUpdate}
            disabled={confirmIsDisabled}
          >
            {confirmLabel}
          </Button>
        )}
      </FlexColumn>
    ),
    [
      cancelLabel,
      confirmIsDisabled,
      confirmLabel,
      options,
      resolvedFooter,
      selectedValue,
      showCancel,
      size,
      titleNode,
      variant,
      fullWidth,
    ]
  );

  const trigger = renderTrigger({
    open,
    onOpen: () => onVisibleChange(true),
    onClose: () => onVisibleChange(false),
    disabled,
  });

  return (
    <Popover
      content={content}
      trigger="click"
      visible={open}
      onVisibleChange={onVisibleChange}
      destroyTooltipOnHide
      {...popoverProps}
    >
      {trigger}
    </Popover>
  );
};

export default RadioPopoverDropdown;
